import { Descope } from "@descope/react-sdk";
import { useCallback, useMemo, useState } from "react";

import { CoAppLoginFlowContainer } from "../global/styled/global.styled";
import EnterEmail from "./login-screens/EnterEmail";
import {
	BYOS_SCREEN_NAMES,
	FLOW_ID,
	INTERACTION,
	SCREEN,
	type ByosScreenName
} from "./login-utils/flowInteractions";
import LoginLayout from "./login-screens/LoginLayout";
import OrganizationPicker from "./login-screens/OrganizationPicker";
import Polling from "./login-screens/Polling";
import PollingSuccess from "./login-screens/PollingSuccess";
import type { FlowNext } from "./login-utils/types";

type FlowScreenState = {
	screenName: string;
	context: Record<string, unknown>;
	next: FlowNext;
	error?: { text?: string; message?: string; description?: string };
};

type DescopeSuccessEvent = CustomEvent<{
	sessionJwt?: string;
	refreshJwt?: string;
	user?: unknown;
}>;

export type CoAppDescopeLoginFlowProps = {
	onSuccess: (e: DescopeSuccessEvent) => void;
	onError?: (e: unknown) => void;
	/** When true, legal copy on EnterEmail is shown (parent has no footer). */
	showEnterEmailLegalCopy?: boolean;
};

/**
 * Descope BYOS sign-in shell: runs the hidden `Descope` flow and overlays
 * custom screens (email, org picker, polling) when `onScreenUpdate` reports a
 * known BYOS screen name.
 */
export default function CoAppDescopeLoginFlow({
	onSuccess,
	onError,
	showEnterEmailLegalCopy = false
}: CoAppDescopeLoginFlowProps) {
	const [flowState, setFlowState] = useState<FlowScreenState | null>(null);
	const [form, setForm] = useState<Record<string, unknown>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const screenName = flowState?.screenName ?? "";
	const customScreenActive = BYOS_SCREEN_NAMES.has(screenName);

	/** Shallow-merge `patch` into the BYOS form object shared across screens. */
	const onFormUpdate = useCallback((patch: Record<string, unknown>) => {
		setForm((prev) => ({ ...prev, ...patch }));
	}, []);

	/**
	 * Descope `onScreenUpdate` handler: persists screen name, context, and flow
	 * `next`; syncs `context.form` and clears submit lock. Returns whether this
	 * screen is rendered by our BYOS UI (vs. default Descope chrome).
	 */
	const onScreenUpdate = useCallback(
		(
			name: string,
			context: Record<string, unknown>,
			next: (interactionId: string, form: Record<string, unknown>) => Promise<unknown>
		) => {
			const error = context?.error as FlowScreenState["error"] | undefined;
			/** Forwards interaction + payload to Descope's native `next` for this step. */
			const advance: FlowNext = async (interactionId, payload) => {
				await next(interactionId, payload ?? {});
			};
			setFlowState({ screenName: name, context, next: advance, error });
			if (context?.form && typeof context.form === "object") {
				setForm((prev) => ({ ...prev, ...(context.form as Record<string, unknown>) }));
			}
			setIsSubmitting(false);
			return BYOS_SCREEN_NAMES.has(name);
		},
		[]
	);

	/**
	 * BYOS screen submit: merges local form with payload, sets submitting
	 * (skipped for `INTERACTION.POLL`), then calls the Descope `next` stored in
	 * `flowState`.
	 */
	const handleNext: FlowNext = useCallback(
		async (interactionId, payload) => {
			if (!flowState?.next) return;
			const isPoll = interactionId === INTERACTION.POLL;
			if (!isPoll) setIsSubmitting(true);
			const merged = { ...form, ...payload };
			if (!isPoll) setForm(merged);
			try {
				await flowState.next(interactionId, merged);
			} finally {
				if (!isPoll) setIsSubmitting(false);
			}
		},
		[flowState, form]
	);

	const errorText =
		flowState?.error?.text ??
		flowState?.error?.message ??
		flowState?.error?.description;

	const flowScreenState = useMemo(() => {
		const raw = flowState?.context?.state;
		return raw !== null && typeof raw === "object"
			? (raw as Record<string, unknown>)
			: undefined;
	}, [flowState?.context?.state]);

	const screenProps = useMemo(
		() => ({
			onNext: handleNext,
			form,
			onFormUpdate,
			errorText,
			context: flowState?.context,
			state: flowScreenState,
			isSubmitting
		}),
		[handleNext, form, onFormUpdate, errorText, flowState?.context, flowScreenState, isSubmitting]
	);

	/** Picks the React BYOS screen component for the current `screenName`. */
	const renderScreen = () => {
		switch (screenName as ByosScreenName) {
			case SCREEN.ENTER_EMAIL:
				return (
					<EnterEmail
						{...screenProps}
						showLegalCopy={showEnterEmailLegalCopy}
					/>
				);
			case SCREEN.ORG_PICKER:
				return <OrganizationPicker {...screenProps} />;
			case SCREEN.POLLING:
				return <Polling {...screenProps} />;
			case SCREEN.POLLING_SUCCESS:
				return <PollingSuccess {...screenProps} />;
			default:
				return null;
		}
	};

	return (
		<CoAppLoginFlowContainer>
			<Descope
				flowId={FLOW_ID}
				theme="light"
				onScreenUpdate={onScreenUpdate}
				onSuccess={onSuccess}
				onError={onError ?? ((e) => console.log("Could not log in!", JSON.stringify(e)))}
			/>
			{customScreenActive && (
				<LoginLayout screenName={screenName} embedded>
					{renderScreen()}
				</LoginLayout>
			)}
		</CoAppLoginFlowContainer>
	);
}
